import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
    Typography, 
    Space, 
    Tag, 
    Button, 
    Spin,
    message,
    Input,
    Modal,
    Tooltip
} from 'antd';
import { 
    DownloadOutlined,
    EyeOutlined,
    FolderOpenOutlined,
    MailOutlined,
    LinkOutlined,
    RollbackOutlined,
    EditOutlined,
    CheckOutlined,
    CloseCircleOutlined,
    CommentOutlined
} from '@ant-design/icons';
import axios from '../../utility/axios';
import dayjs from 'dayjs';
import { useAuth } from '../../hooks/useAuth';
import AttachmentPreview from '../../contexts/AttachmentPreview';
import { isConvertible } from '../../utility/fileTypes';
import '../../styles/documents/DocumentPreview.css';
import '../../styles/documents/DocumentRequestRecords.css';

const { Title, Text } = Typography;

const DocumentPreview = () => {
    const { t } = useTranslation();
    const { unique_identifier } = useParams();
    const [documentData, setDocumentData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [documentFiles, setDocumentFiles] = useState([]);
    const [documentReceivers, setDocumentReceivers] = useState([]);
    const [systemUrl, setSystemUrl] = useState('');
    const [showDenyModal, setShowDenyModal] = useState(false);
    const [denyReason, setDenyReason] = useState('');
    const [currentDenyingFileId, setCurrentDenyingFileId] = useState(null);
    const [attachmentPreviewDoc, setAttachmentPreviewDoc] = useState(null);
    const [systemSettings, setSystemSettings] = useState(null);
    const navigate = useNavigate();
    useLocation();
    const { user } = useAuth();
    const documentLink = `${window.location.origin}/document/${unique_identifier}`;
    
    // Resizable columns state
    const [columnWidths, setColumnWidths] = useState(() => {
        const saved = localStorage.getItem('docpreview_column_widths');
        return saved ? JSON.parse(saved) : { body: 60, files: 40 };
    });
    const [isResizing, setIsResizing] = useState(false);
    const contentRowRef = useRef(null);

    useEffect(() => {
        const fetchDocument = async () => {
            try {
                const response = await axios.get(`/api/document/preview/${unique_identifier}/`);
                setDocumentData(response.data.message);
                // Sort document files by title alphabetically (ASC)
                const sortedFiles = (response.data.message.document_files || []).sort((a, b) =>
                    (a.document_name || a.title || '').localeCompare(b.document_name || b.title || '')
                );
                setDocumentFiles(sortedFiles);
                setDocumentReceivers(response.data.document_receivers || []);
                setSystemUrl(response.data.system_url || window.location.origin);
            } catch (error) {
                // Access the error message from the backend response
                const errorMessage = error.response?.data?.error || t('documentPreview.errors.loadFailed');
                message.error(errorMessage);
            } finally {
                setLoading(false);
            }
        };

        fetchDocument();
    }, [unique_identifier, t]);

    const fetchSystemSettings = async () => {
        try {
            const response = await axios.get('/api/system-settings/');
            setSystemSettings({
                allowed_file_types: response.data.allowedFileTypes,
                max_upload_size: response.data.maxUploadSize
            });
        } catch (err) {
            console.error('Failed to fetch system settings:', err);
        }
    };

    useEffect(() => {
        fetchSystemSettings();
    }, []);

    // Handle column resize
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isResizing || !contentRowRef.current) return;
            
            const containerRect = contentRowRef.current.getBoundingClientRect();
            const containerWidth = containerRect.width;
            const mouseX = e.clientX - containerRect.left;
            const mousePercent = (mouseX / containerWidth) * 100;
            
            // Constrain body width between 30% and 80%
            let bodyWidth = Math.max(30, Math.min(80, mousePercent));
            const filesWidth = 100 - bodyWidth;
            
            const newWidths = { body: bodyWidth, files: filesWidth };
            setColumnWidths(newWidths);
            localStorage.setItem('docpreview_column_widths', JSON.stringify(newWidths));
        };

        const handleMouseUp = () => {
            if (isResizing) {
                setIsResizing(false);
                document.body.classList.remove('docpreview-resizing');
            }
        };

        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.classList.add('docpreview-resizing');
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.classList.remove('docpreview-resizing');
        };
    }, [isResizing]);

    const handleDownload = async (documentFileId, fileName) => {
        try {
            const response = await axios.get(`/api/documents/download/${documentFileId}/`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            message.error(t('documentPreview.errors.downloadFailed'));
        }
    };

    const handleDownloadAll = async () => {
        try {
            const response = await axios.get(`/api/documents/${documentData.unique_identifier}/download-all/`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'documents.zip');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            message.error(t('documentPreview.errors.downloadFailed'));
        }
    };

    const handleCopy = (linkToCopy) => {
        try {
            // Check if navigator.clipboard is available
            if (!navigator.clipboard) {
                // Fallback copy method using document.execCommand
                const textArea = document.createElement('textarea');
                textArea.value = linkToCopy;
                document.body.appendChild(textArea);
                textArea.select();
                const successful = document.execCommand('copy');
                document.body.removeChild(textArea);
                
                if (successful) {
                    message.success(t('documentPreview.linkCopied'));
                } else {
                    message.error(t('documentPreview.errors.copyFailed'));
                }
                return;
            }
            
            // Use the Clipboard API
            navigator.clipboard.writeText(linkToCopy)
                .then(() => message.success(t('documentPreview.linkCopied')))
                .catch((err) => {
                    console.error('Could not copy text: ', err);
                    message.error(t('documentPreview.errors.copyFailed'));
                });
        } catch (err) {
            console.error('Copy error: ', err);
            message.error(t('documentPreview.errors.copyFailed'));
        }
    };

    const handleEdit = async () => {
        try {
            // Call the API to check if the document can be fully edited or limited edited
            const response = await axios.post(`/api/document/edit/${unique_identifier}/`);
            
            if (response.data.can_edit_all) {
                // Show success message for moving to drafts
                message.success(t('documentPreview.movedToDrafts'));
                // For unread documents, navigate to the draft edit form
                navigate(`/document-compose?draft_id=${response.data.draft_id}`);
            } else {
                // For read documents, navigate with limited edit parameters
                const currentValues = response.data.current_values;
                const documentData = response.data.document_data || {};
                
                // Store the limited edit data in localStorage with all document data
                localStorage.setItem('limitedEditDocument', JSON.stringify({
                    documentId: response.data.document_id,
                    self_destruct: currentValues.self_destruct,
                    ttlive: currentValues.ttlive,
                    limitedEdit: true,
                    documentData: documentData
                }));
                
                // Navigate to the document-compose page with a special parameter
                navigate(`/document-compose?limited_edit=${response.data.document_id}`);
            }
        } catch (error) {
            const errorMessage = error.response?.data?.status || 
                                error.response?.data?.error ||
                                t('documentPreview.errors.editFailed');
            message.error(errorMessage);
        }
    };

    const handleApproveDocument = async (documentFileId) => {
        try {
            await axios.post(`/api/approve_deny_document_file/${documentFileId}/`, {
                action: 'approved'
            });
            message.success(t('documentPreview.documentApproved'));
            
            // Refresh the document data
            const response = await axios.get(`/api/document/preview/${unique_identifier}/`);
            setDocumentData(response.data.message);
            // Sort document files by title alphabetically (ASC)
            const sortedFiles = (response.data.message.document_files || []).sort((a, b) =>
                (a.document_name || a.title || '').localeCompare(b.document_name || b.title || '')
            );
            setDocumentFiles(sortedFiles);
            setDocumentReceivers(response.data.document_receivers || []);
        } catch (error) {
            const errorMessage = error.response?.data?.error || t('documentPreview.errors.approveFailed');
            message.error(errorMessage);
        }
    };

    const handleDenyDocument = (documentFileId) => {
        setCurrentDenyingFileId(documentFileId);
        setDenyReason('');
        setShowDenyModal(true);
    };

    const handleConfirmDeny = async () => {
        if (!denyReason.trim()) {
            message.error(t('documentPreview.denyReasonRequired'));
            return;
        }

        try {
            await axios.post(`/api/approve_deny_document_file/${currentDenyingFileId}/`, {
                action: 'denied',
                deny_reason: denyReason
            });
            message.success(t('documentPreview.documentDenied'));
            
            // Reset modal state
            setShowDenyModal(false);
            setDenyReason('');
            setCurrentDenyingFileId(null);
            
            // Refresh the document data
            const response = await axios.get(`/api/document/preview/${unique_identifier}/`);
            setDocumentData(response.data.message);
            // Sort document files by title alphabetically (ASC)
            const sortedFiles = (response.data.message.document_files || []).sort((a, b) =>
                (a.document_name || a.title || '').localeCompare(b.document_name || b.title || '')
            );
            setDocumentFiles(sortedFiles);
            setDocumentReceivers(response.data.document_receivers || []);
        } catch (error) {
            const errorMessage = error.response?.data?.error || t('documentPreview.errors.denyFailed');
            message.error(errorMessage);
        }
    };

    const handleCancelDeny = () => {
        setShowDenyModal(false);
        setDenyReason('');
        setCurrentDenyingFileId(null);
    };

    const getLinkForReceiver = (receiver) => receiver?.access_link ?? null;

    const renderRecipientWithLink = () => {
        if (documentReceivers && documentReceivers.length > 0) {
            return (
                <div className="document-preview-row">
                    <Text strong>{t('documentPreview.to')} </Text>
                    <div className="document-preview-recipients-list">
                        {documentReceivers.map((receiver) => {
                            const recipientLink = getLinkForReceiver(receiver);
                            return (
                                <span key={receiver.email} className="document-preview-recipient-with-link">
                                    <span>{receiver.email}</span>
                                    {recipientLink && (
                                        <Tooltip
                                            title={recipientLink}
                                            placement="top"
                                            mouseEnterDelay={0.3}
                                            overlayStyle={{ maxWidth: '700px' }}
                                        >
                                            <LinkOutlined
                                                className="document-preview-recipient-link-icon"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCopy(recipientLink);
                                                }}
                                                aria-label={t('documentPreview.copyLink')}
                                            />
                                        </Tooltip>
                                    )}
                                </span>
                            );
                        })}
                    </div>
                </div>
            );
        }
        return (
            <div className="document-preview-row">
                <Text strong>{t('documentPreview.to')} </Text>
                <div className="document-preview-recipients-list">
                    <span className="document-preview-recipient-with-link">
                        <span>{documentData?.receiver || ''}</span>
                        {documentLink && (
                            <Tooltip
                                title={documentLink}
                                placement="top"
                                mouseEnterDelay={0.3}
                                overlayStyle={{ maxWidth: '700px' }}
                            >
                                <LinkOutlined
                                    className="recipient-link-icon"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopy(documentLink);
                                    }}
                                    aria-label={t('documentPreview.copyLink')}
                                />
                            </Tooltip>
                        )}
                    </span>
                </div>
            </div>
        );
    };

    const renderDenyModal = () => {
        const currentFile = documentFiles.find(file => file.unique_identifier === currentDenyingFileId);
        const documentName = currentFile?.document_name || currentFile?.title || 'document';
        
        return (
            <Modal
                title={
                    <Space>
                        <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                        {t('documentPreview.rejectingDocument', { documentName })}
                    </Space>
                }
                open={showDenyModal}
                onOk={handleConfirmDeny}
                onCancel={handleCancelDeny}
                okText={t('documentPreview.confirmDeny')}
                cancelText={t('documentPreview.cancel')}
                okButtonProps={{ danger: true }}
                width={600}
            >
                <div style={{ marginBottom: 16 }}>
                    <Text>{t('documentPreview.denyReasonPrompt')}</Text>
                </div>
                <Input.TextArea
                    value={denyReason}
                    onChange={(e) => setDenyReason(e.target.value)}
                    placeholder={t('documentPreview.denyReasonPlaceholder')}
                    rows={4}
                    maxLength={500}
                />
            </Modal>
        );
    };

    if (loading) {
        return (
            <div className="document-preview-loading">
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="document-preview-container">
            {/* Top section - matches MessagePreview */}
            <div className="document-preview-top-header">
                <Button 
                    type="text" 
                    icon={<RollbackOutlined />}
                    onClick={() => navigate('/profile/mailbox')}
                    className="back-to-mailbox-btn"
                >
                    {t('documentPreview.backToMailbox')}
                </Button>
                <div className="document-preview-action-buttons">
                    {documentData && user?.email === documentData.sender && (
                                <Button 
                                    icon={<EditOutlined />}
                                    onClick={handleEdit}
                                    className="action-btn"
                                >
                                    {t('documentPreview.edit')}
                                </Button>
                    )}
                </div>
            </div>

            {documentData && (
                <>
                    {/* Detail + Body section - matches MessagePreview */}
                    <div className="document-preview-detail-body-wrapper">
                    {documentData.self_destruct && (
                        <div className="preview-self-destruct-warning">
                            {t('documentPreview.selfDestructWarning')}
                        </div>
                    )}
                    {/* Email Info Section */}
                    <div className="document-preview-email-info">
                        <div className="document-preview-icon-wrapper">
                            <MailOutlined />
                        </div>
                        <div className="document-preview-details">
                            <div className="document-preview-sender-receiver">
                                <div className="document-preview-row">
                                    <Text strong>{t('documentPreview.from')} </Text>
                                    <Text>{documentData.sender}</Text>
                                </div>
                                {renderRecipientWithLink()}
                            </div>
                        </div>

                        {/* Metadata on the right side */}
                        <div className="document-preview-metadata-side">
                            <div className="metadata-item">
                                <div className="metadata-label">{t('documentPreview.expires')}</div>
                                <div className="metadata-value">
                                    {dayjs(documentData.ttlive).format('MMM D, YYYY, h:mm A')}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Subject Line */}
                    <div className="document-preview-subject-section">
                        <Title level={3} className="subject-title">
                            {documentData.subject}
                        </Title>
                    </div>


                    {/* Two-column layout: Body + Document Files */}
                    <div className="document-preview-content-row" ref={contentRowRef}>
                        {/* Document Body - Left Column */}
                        <div 
                            className="document-preview-body-column"
                            style={{ width: `${columnWidths.body}%` }}
                        >
                            {documentData.body && (
                                <div className="document-preview-body-section">
                                    <div 
                                        className="document-body-content"
                                        dangerouslySetInnerHTML={{ __html: documentData.body }}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Resize Handle */}
                        {documentFiles.length > 0 && (
                            <div 
                                className="docpreview-resize-handle"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    setIsResizing(true);
                                }}
                            />
                        )}

                        {/* Document Files - Right Column */}
                        {documentFiles.length > 0 && (
                            <div 
                                className="document-preview-files-column"
                                style={{ width: `${columnWidths.files}%` }}
                            >
                                <div className="docpreview-files-list-section">
                                    <div className="docpreview-files-section-header">
                                        <span className="docpreview-files-section-label">
                                            {t('documents.view.requestedDocumentsLabel')}
                                        </span>
                                        <div className="docpreview-files-section-header-right">
                                            <span className="docpreview-files-section-counter">
                                                {(() => {
                                                    const totalCount = documentFiles.length;
                                                    const uploadedCount = documentFiles.filter(f => f.document_name).length;
                                                    const showSent = uploadedCount === totalCount && totalCount > 0;
                                                    return showSent
                                                        ? `${uploadedCount} / ${totalCount} ${t('documents.view.documentsSent')}`
                                                        : `${uploadedCount} / ${totalCount} ${t('documents.view.documentsUploaded')}`;
                                                })()}
                                            </span>
                                            {systemSettings && (
                                                <span className="docpreview-files-section-max-size">
                                                    {t('documents.view.maxFileSize')}: {systemSettings.max_upload_size ? `${systemSettings.max_upload_size}MB` : t('documents.view.noLimit')}
                                                </span>
                                            )}
                                            {documentFiles.filter(f => f.document_name).length >= 1 && (
                                                <Tooltip title={t('documentPreview.downloadAll')}>
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        icon={<FolderOpenOutlined />}
                                                        onClick={handleDownloadAll}
                                                        className="docpreview-files-download-all-btn"
                                                    />
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>
                                    <div className="docpreview-files-list">
                                        {documentFiles.map((documentFile, index) => {
                                            const hasUploadedFile = documentFile.has_been_read === true;
                                            const canPreview = hasUploadedFile && isConvertible(documentFile.document_name);
                                            const tooltipContent = (
                                                <div>
                                                    <div>{t('documentPreview.returnType')}: {documentFile.return_type}</div>
                                                    {hasUploadedFile && documentFile.document_type && (
                                                        <div>{t('documentPreview.documentType') || 'Document Type'}: {documentFile.document_type}</div>
                                                    )}
                                                    {documentFile.document_status === 'denied' && documentFile.document_denied_reason && (
                                                        <div style={{ color: '#ff4d4f', marginTop: '4px' }}>{documentFile.document_denied_reason}</div>
                                                    )}
                                                </div>
                                            );
                                            
                                            return (
                                            <div key={index} className="docpreview-file-row-wrapper">
                                            <Tooltip 
                                                title={tooltipContent}
                                                placement="top"
                                            >
                                            <div
                                                className={`docpreview-file-row ${canPreview ? 'docpreview-file-row-previewable' : ''} ${canPreview && (documentFile.comment_count ?? 0) > 0 ? 'docpreview-file-row-has-comments' : ''}`}
                                                onClick={canPreview ? (e) => {
                                                    if (!e.target.closest('.docpreview-file-actions')) {
                                                        setAttachmentPreviewDoc({ documentFileId: documentFile.unique_identifier, fileName: documentFile.document_name });
                                                    }
                                                } : undefined}
                                                role={canPreview ? 'button' : undefined}
                                                tabIndex={canPreview ? 0 : undefined}
                                                onKeyDown={canPreview ? (e) => {
                                                    if ((e.key === 'Enter' || e.key === ' ') && !e.target.closest('.docpreview-file-actions')) {
                                                        e.preventDefault();
                                                        setAttachmentPreviewDoc({ documentFileId: documentFile.unique_identifier, fileName: documentFile.document_name });
                                                    }
                                                } : undefined}
                                            >
                                                {canPreview && (documentFile.comment_count ?? 0) > 0 && (
                                                    <span
                                                        className="docpreview-file-comment-badge"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setAttachmentPreviewDoc({ documentFileId: documentFile.unique_identifier, fileName: documentFile.document_name });
                                                        }}
                                                        role="button"
                                                        tabIndex={0}
                                                        onKeyDown={(e) => {
                                                            if ((e.key === 'Enter' || e.key === ' ') && (documentFile.comment_count ?? 0) > 0) {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                setAttachmentPreviewDoc({ documentFileId: documentFile.unique_identifier, fileName: documentFile.document_name });
                                                            }
                                                        }}
                                                        aria-label={`${documentFile.comment_count} comment${(documentFile.comment_count ?? 0) !== 1 ? 's' : ''}`}
                                                    >
                                                        <CommentOutlined />
                                                        <span className="docpreview-file-comment-badge-count">{documentFile.comment_count}</span>
                                                    </span>
                                                )}
                                                <span className="docpreview-file-title">
                                                    {documentFile.title}
                                                    {hasUploadedFile && documentFile.document_name && (
                                                        <span className="docpreview-file-uploaded-name"> : {documentFile.document_name}</span>
                                                    )}
                                                </span>
                                                <div className="docpreview-file-right">
                                                <div className="docpreview-file-status">
                                                    {documentFile.document_name ? (
                                                        <Tag 
                                                            color={
                                                                documentFile.document_status === 'approved' ? 'green' :
                                                                documentFile.document_status === 'denied' ? 'red' : 'orange'
                                                            }
                                                            className="docpreview-file-status-tag"
                                                        >
                                                            {documentFile.document_status === 'approved' 
                                                                ? t('documentPreview.statusApproved')
                                                                : documentFile.document_status === 'denied'
                                                                ? t('documentPreview.statusDenied')
                                                                : t('documentPreview.statusPending')}
                                                        </Tag>
                                                    ) : (
                                                        <Tag color="default" className="docpreview-file-status-tag">
                                                            {t('documents.view.statusNotSent')}
                                                        </Tag>
                                                    )}
                                                </div>
                                                <div className="docpreview-file-actions">
                                                    <div className="docpreview-file-preview">
                                                        {canPreview ? (
                                                            <Button 
                                                                type="text"
                                                                size="small"
                                                                icon={<EyeOutlined />}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setAttachmentPreviewDoc({ documentFileId: documentFile.unique_identifier, fileName: documentFile.document_name });
                                                                }}
                                                                className="docpreview-file-action-btn docpreview-file-preview-btn"
                                                            />
                                                        ) : (
                                                            <span className="docpreview-file-action-disabled">
                                                                <EyeOutlined />
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="docpreview-file-download">
                                                        {hasUploadedFile ? (
                                                            <Button 
                                                                type="text"
                                                                size="small"
                                                                icon={<DownloadOutlined />}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDownload(documentFile.unique_identifier, documentFile.document_name);
                                                                }}
                                                                className="docpreview-file-action-btn docpreview-file-download-btn"
                                                            />
                                                        ) : (
                                                            <span className="docpreview-file-action-disabled">
                                                                <DownloadOutlined />
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="docpreview-file-approve">
                                                        {hasUploadedFile && documentFile.document_status !== 'approved' ? (
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={<CheckOutlined />}
                                                                onClick={() => handleApproveDocument(documentFile.unique_identifier)}
                                                                className="docpreview-file-action-btn docpreview-file-approve-btn"
                                                            />
                                                        ) : (
                                                            <span className="docpreview-file-action-disabled">
                                                                <CheckOutlined />
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="docpreview-file-deny">
                                                        {hasUploadedFile ? (
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={<CloseCircleOutlined />}
                                                                onClick={() => handleDenyDocument(documentFile.unique_identifier)}
                                                                className={`docpreview-file-action-btn docpreview-file-deny-btn ${documentFile.document_status === 'denied' ? 'docpreview-file-deny-btn-denied' : ''}`}
                                                            />
                                                        ) : (
                                                            <span className="docpreview-file-action-disabled">
                                                                <CloseCircleOutlined />
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                </div>
                                            </div>
                                            </Tooltip>
                                            </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    </div>
                    </>
                )}
            
            {renderDenyModal()}

            <AttachmentPreview
                open={!!attachmentPreviewDoc}
                onClose={() => setAttachmentPreviewDoc(null)}
                documentFileId={attachmentPreviewDoc?.documentFileId}
                fileName={attachmentPreviewDoc?.fileName}
                onDownload={attachmentPreviewDoc ? () => handleDownload(attachmentPreviewDoc.documentFileId, attachmentPreviewDoc.fileName) : undefined}
            />
        </div>
    );
};

export default DocumentPreview;